import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Badge,
  Box,
  ButtonBase,
  CardContent,
  ClickAwayListener,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';

// project-imports
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import useAuth from 'hooks/useAuth';
import useRoles from 'hooks/useRoles';
import { ThemeMode } from 'config';

// assets
import { Logout, Profile } from 'iconsax-react';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //


const ProfilePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { logout, user } = useAuth();
  const { roleLabel } = useRoles();

  const { role, firstName, lastName } = user || {};
  const avatarLetters =
    (firstName?.[0]?.toUpperCase() || '') +
    (lastName?.[0]?.toUpperCase() || '');

  const handleLogout = async () => {
    try {
      await logout();
      navigate(`/login`, {
        state: {
          from: '',
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={{
          p: 0.25,
          borderRadius: 1,
          '&:hover': {
            bgcolor:
              theme.palette.mode === ThemeMode.DARK
                ? 'secondary.light'
                : 'secondary.lighter',
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.secondary.dark}`,
            outlineOffset: 2,
          },
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge color="error" overlap="circular" variant="dot">
          <Avatar alt="profile" type="outlined">
            {avatarLetters}
          </Avatar>
        </Badge>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions
            type="grow"
            position="top-right"
            in={open}
            {...TransitionProps}
          >
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: 260,
                minWidth: 240,
                maxWidth: 260,
                [theme.breakpoints.down('md')]: {
                  maxWidth: 250,
                },
                borderRadius: 1.5,
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3, pb: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar alt="profile" type="outlined">
                        {avatarLetters}
                      </Avatar>
                      <Stack>
                        <Typography variant="subtitle1">
                          {firstName || ''} {lastName || ''}
                        </Typography>
                        <Typography variant="body2" color="secondary">
                          {roleLabel(role)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>

                  <Divider />

                  <Box sx={{ p: 1 }}>
                    <List
                      component="nav"
                      sx={{ p: 0, '& .MuiListItemIcon-root': { minWidth: 32 } }}
                    >
                      <ListItemButton
                        onClick={() => {
                          setOpen(false);
                          navigate('/profile');
                        }}
                      >
                        <ListItemIcon>
                          <Profile variant="Bulk" size={18} />
                        </ListItemIcon>
                        <ListItemText primary="View Profile" />
                      </ListItemButton>
                      <ListItemButton onClick={handleLogout}>
                        <ListItemIcon>
                          <Logout variant="Bulk" size={18} />
                        </ListItemIcon>
                        <ListItemText primary="Logout" />
                      </ListItemButton>
                    </List>
                  </Box>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default ProfilePage;
